import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment as ENV } from '../../environments/environment';
import { Utils } from '../core/Utils';
import {
    BcContestProbability, IBcActiveContest, IBcAvailableContest, IBcContest, IBcContestEditModel,
    IBcContestEvent, IBcContestStudentData, IBcContestStudentCourse, IBcContestCourse, IBcContestStudent,
    IBcContestApplication, BcContestResult, IBcContestFilters, IBcContestProgram
} from '../models/BcContest';
import { IPagingParams } from '../models/Common';

/**
 * BC contest service
 */
@Injectable({ providedIn: "root" })
export class BcContestService {
    constructor(public http: HttpClient) { }

    public get apiUrl(): string { return `${ENV.apiUrl}/bcContests` }

    /**
     * Get contests.
     */
    get(): Observable<IBcContest[]> {
        return this.http.get<IBcContest[]>(this.apiUrl);
    }

    /**
     * Get contest by ID.
     * @param id Contest ID
     */
    getById(id: number): Observable<IBcContest> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<IBcContest>(url);
    }

    /**
     * Get available contests from SIS.
     */
    getAvailableContests(): Observable<IBcAvailableContest[]> {
        const url = `${this.apiUrl}/available`;
        return this.http.get<IBcAvailableContest[]>(url);
    }

    /**
     * Get contests courses.
     * @param id Contest ID
     */
    getCourses(id: number): Observable<IBcContestCourse[]> {
        const url = `${this.apiUrl}/${id}/courses`;
        return this.http.get<IBcContestCourse[]>(url);
    }

    /**
     * Sync contest courses from SIS.
     * @param id Contest ID
     */
    syncCourses(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/courses/sync`;
        return this.http.post(url, null);
    }

    /**
     * Get contest students.
     * @param id Contest ID
     * @param filter Filter values
     * @param paging
     */
    getStudents(id: number, filter?: {
        student?: string,
        programs?: string[],
        creditPoints?: number[],
        countFrom?: number,
        countTo?: number,
        gradeFrom?: number,
        gradeTo?: number,
        customGradeFrom?: number,
        customGradeTo?: number
    }, paging?: IPagingParams): Observable<{ items: IBcContestStudent[], total: number }> {
        const url = `${this.apiUrl}/${id}/students`;
        return this.http.get<{ items: IBcContestStudent[], total: number }>(url, {
            params: Utils.sanitizeQueryParams({
                ...filter,
                ...Utils.sanitizeQueryParams(paging)
            })
        });
    }

    /**
     * Get contest programs.
     * @param id
     * @returns
     */
    getPrograms(id: number): Observable<IBcContestProgram[]> {
        const url = `${this.apiUrl}/${id}/programs`;
        return this.http.get<IBcContestProgram[]>(url);
    }

    /**
     * Sync contest students from SIS.
     * @param id Contest ID
     */
    syncStudents(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/students/sync`;
        return this.http.post(url, null);
    }

    /**
     * Create a new contest.
     * @param model Contest data
     */
    create(model: IBcContestEditModel): Observable<number> {
        return this.http.post<number>(this.apiUrl, model);
    }

    /**
     * Update a contest.
     * @param id Contest ID
     * @param model Contest data
     */
    update(id: number, model: IBcContestEditModel): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.put(url, model);
    }

    /**
     * Publish a contest.
     * @param id Contest ID
     */
    publish(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/publish`;
        return this.http.post(url, null);
    }

    /**
     * Get active contest.
     */
    getActive(): Observable<IBcActiveContest> {
        const url = `${this.apiUrl}/active`;
        return this.http.get<IBcActiveContest>(url);
    }

    /**
     * Apply to a contest.
     * @param id Contest ID
     * @param studentId Student speciality ID
     * @returns New contest student ID
     */
    apply(id: number, studentId: string): Observable<number> {
        const url = `${this.apiUrl}/${id}/apply`;
        return this.http.post<number>(url, { studentId });
    }

    /**
     * Get student contest data.
     * @param id Contest ID
     * @param upn Student UPN
     */
    getStudentData(id: number, upn?: string): Observable<IBcContestStudentData[]> {
        const url = `${this.apiUrl}/${id}/studentData`;
        return this.http.get<IBcContestStudentData[]>(url, { params: Utils.sanitizeQueryParams({ upn }) });
    }

    /**
     * Get student courses.
     * @param studentId Contest student ID
     * @param all Return all available courses, otherwise only those to which student has applied
     */
    getStudentCourses(studentId: number, all: boolean = false): Observable<IBcContestStudentCourse[]> {
        const url = `${this.apiUrl}/students/${studentId}/courses`;

        const params: any = {};
        if (all) params.all = true;

        return this.http.get<IBcContestStudentCourse[]>(url, { params });
    }

    /**
     * Get course group events.
     * @param groupId Course group ID
     */
    getGroupEvents(groupId: string): Observable<IBcContestEvent[]> {
        return this.http.get<IBcContestEvent[]>(`${this.apiUrl}/groups/${groupId}/events`);
    }

    /**
     * Add student application.
     * @param studentId Contest student ID
     * @param subjectSemesterId Subject semester ID
     * @param groupId Group ID
     * @returns New application ID, probability, and total application count in the course/group
     */
    addApplication(studentId: number, subjectSemesterId: string, groupId?: string): Observable<{
        Id: number,
        Probability: BcContestProbability,
        ApplicationCount: number
    }> {
        const url = `${this.apiUrl}/students/${studentId}/applications`;
        return this.http.post<{
            Id: number,
            Probability: BcContestProbability,
            ApplicationCount: number
        }>(url, {
            subjectSemesterId,
            groupId
        });
    }

    /**
     * Remove student application.
     * @param applicationId Application ID
     * @returns Application count in the course/group after removal
     */
    removeApplication(applicationId: number): Observable<{ ApplicationCount: number }> {
        const url = `${this.apiUrl}/applications/${applicationId}`;
        return this.http.delete<{ ApplicationCount: number }>(url);
    }

    /**
     * Update student preferences.
     * @param studentId Contest student ID
     * @param data Preferences
     */
    updatePreferences(studentId: number, data: {
        /**
         * Application IDs prioritized from highest priority to lowest
         */
        applicationPriority: number[]
    }): Observable<{ Id: number, Priority: number }[]> {
        const url = `${this.apiUrl}/students/${studentId}/preferences`;
        return this.http.put<{ Id: number, Priority: number }[]>(url, data);
    }

    /**
     * Update student data.
     * @param studentId Contest student ID
     * @param data Student data
     */
    updateStudent(studentId: number, data: {
        customGrade?: number
    }): Observable<any> {
        const url = `${this.apiUrl}/students/${studentId}`;
        return this.http.put<any>(url, data);
    }

    /**
     * Update application data.
     * @param applicationId Contest application ID
     * @param data Application data
     */
    updateApplication(applicationId: number, data: {
        result: BcContestResult
    }): Observable<any> {
        const url = `${this.apiUrl}/applications/${applicationId}`;
        return this.http.put<any>(url, data);
    }

    /**
     * Calculate contest results.
     * @param id Contest ID
     */
    calculate(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}/calculate`;
        return this.http.post(url, null);
    }

    /**
     * Submit results to SIS and complete the contest.
     * @param id Contest ID
     * @param programs Specific program codes to submit results for
     */
    submit(id: number, programs?: string[]): Observable<any> {
        const url = `${this.apiUrl}/${id}/submit`;
        return this.http.post(url, { programs });
    }

    /**
     * Get contest applications.
     * @param id Contest ID
     * @param filter
     */
    getApplications(id: number, filter: {
        courseId?: number,
        studentId?: number,
        hasError?: boolean
    }, paging?: IPagingParams): Observable<{ items: IBcContestApplication[], total: number }> {
        const url = `${this.apiUrl}/${id}/applications`;
        return this.http.get<{ items: IBcContestApplication[], total: number }>(url, {
            params: {
                ...Utils.sanitizeQueryParams(filter),
                ...Utils.sanitizeQueryParams(paging)
            }
        });
    }

    /**
     * Get contest filters.
     * @param id Contest ID
     */
    getFilters(id: number): Observable<IBcContestFilters> {
        const url = `${this.apiUrl}/${id}/filters`;
        return this.http.get<IBcContestFilters>(url);
    }
}
